/**
 * Service Provider application — Profile → Services tab.
 *
 * Listed for every member (the tab is no longer gated on the Promoter member type);
 * the answers are submitted to the VL admin database (`POST /member/services`) for
 * approval, and the member's Member Type is snapshotted with the submission.
 *
 * Q4 (`PROMOTER_SERVICE_TYPE_OPTIONS`) is also the catalog the public `/services`
 * directory filters by ("Select performer types").
 */

/** Intro answer limit (Q — introduction). */
export const PROMOTER_APPLICATION_INTRO_MAX = 2000;

export interface PromoterApplicationOption {
  id: string;
  label: string;
  /** Emoji badge rendered next to the label where the UI supports it (service types). */
  emoji?: string;
}

/** Q4 — What services do you offer? (performer / service-provider types) */
export const PROMOTER_SERVICE_TYPE_OPTIONS: PromoterApplicationOption[] = [
  { id: "band", label: "Band", emoji: "🎸" },
  { id: "bartender", label: "Bartender", emoji: "🍸" },
  { id: "chef", label: "Chef", emoji: "👨‍🍳" },
  { id: "comedian", label: "Comedian", emoji: "😂" },
  { id: "dancer", label: "Dancer", emoji: "💃" },
  { id: "dj", label: "DJ", emoji: "🎧" },
  { id: "event-planner", label: "Event Planner", emoji: "📋" },
  { id: "magician", label: "Magician", emoji: "🪄" },
  { id: "mc", label: "MC", emoji: "🎤" },
  { id: "models", label: "Model", emoji: "✨" },
  { id: "musician", label: "Musician", emoji: "🎹" },
  { id: "photographer", label: "Photographer", emoji: "📸" },
  { id: "promoter", label: "Promoter", emoji: "📣" },
  { id: "singer", label: "Singer", emoji: "🎙️" },
];

/** Q5 — What type of events do you promote? */
export const PROMOTER_EVENT_TYPE_OPTIONS: PromoterApplicationOption[] = [
  { id: "nightclub", label: "Nightclub" },
  { id: "day-party", label: "Day Party" },
  { id: "rooftop", label: "Rooftop" },
  { id: "pool-party", label: "Pool Party" },
  { id: "concert", label: "Concert" },
  { id: "festival", label: "Festival" },
  { id: "brunch", label: "Brunch" },
  { id: "dinner", label: "Dinner" },
  { id: "private-party", label: "Private Party" },
  { id: "birthday", label: "Birthday" },
  { id: "corporate", label: "Corporate" },
  { id: "fashion", label: "Fashion" },
  { id: "album-release", label: "Album Release" },
  { id: "sports", label: "Sports Watch Party" },
  { id: "networking", label: "Networking" },
  { id: "after-work", label: "After Work" },
  { id: "speed-dating", label: "Speed Dating" },
  { id: "wine-tasting", label: "Wine Tasting" },
];

/** Q6 — What music styles do you promote? */
export const PROMOTER_MUSIC_STYLE_OPTIONS: PromoterApplicationOption[] = [
  { id: "hiphop", label: "Hip-Hop / Rap" },
  { id: "rnb", label: "R&B" },
  { id: "afrobeat", label: "Afrobeat" },
  { id: "amapiano", label: "Amapiano" },
  { id: "house", label: "House" },
  { id: "tech-house", label: "Tech House" },
  { id: "techno", label: "Techno" },
  { id: "edm", label: "EDM / Big Room" },
  { id: "latin", label: "Latin / Reggaeton" },
  { id: "pop", label: "Pop / Top 40" },
  { id: "disco", label: "Disco / Funk" },
  { id: "dnb", label: "Drum & Bass" },
  { id: "dancehall", label: "Dancehall" },
  { id: "open-format", label: "Open Format" },
];

/** Q7 — What help do you need to increase your exposure and sales? */
export const PROMOTER_HELP_OPTIONS: PromoterApplicationOption[] = [
  { id: "featured-placement", label: "Featured placement on Listaa" },
  { id: "venue-intros", label: "Venue introductions" },
  { id: "email-sms", label: "Email & SMS campaigns" },
  { id: "ads-budget", label: "Paid ads budget" },
  { id: "content", label: "Professional photos & video" },
  { id: "event-leads", label: "Event & ticket leads" },
  { id: "analytics", label: "Analytics & reporting" },
  { id: "onboarding", label: "Onboarding call" },
  { id: "co-marketing", label: "Co-marketing with venues" },
  { id: "sub-promoters", label: "Recruiting sub-promoters" },
];

/** Q8 — What services or tools do you currently use? */
export const PROMOTER_TOOL_OPTIONS: PromoterApplicationOption[] = [
  { id: "guest-list-app", label: "Guest list app" },
  { id: "ticketing", label: "Ticket platform" },
  { id: "crm", label: "CRM / email tool" },
  { id: "whatsapp-sms", label: "WhatsApp / SMS blasts" },
  { id: "social-ads", label: "Instagram / TikTok ads" },
  { id: "link-in-bio", label: "Link-in-bio page" },
  { id: "table-system", label: "Table reservation system" },
  { id: "design", label: "Design tools (Canva…)" },
  { id: "spreadsheets", label: "Spreadsheets" },
  { id: "none", label: "None yet" },
];
